import { isLoaded } from "../systems/memory.js";
import { GAME_CONFIG } from "../config/gameConfig.js";
import {
    getFleeTargetAwayFrom,
    getLevel,
    getSightEndPoint,
    hasLineOfSightWorld,
    isWalkableWorldPos,
    resolveWalkablePos,
} from "../systems/level.js";
import { getRoomAt } from "../systems/rooms.js";
import { spawnMovementDust } from "../systems/dust.js";
import { lockAlarmRoom } from "../systems/alarmLockdown.js";
import { lockCutscene, unlockCutscene } from "../systems/cutsceneState.js";
import { isGameplayPaused } from "../systems/pauseState.js";
import { showHumanDialogue } from "../ui/humanDialogueUI.js";
import { isHumanDetectionGraceActive } from "../systems/vision.js";
import { AUDIO_SOUNDS, playAlarmFromLights, playAt } from "../systems/audio.js";
import { spawnFirstRamStickPickup, spawnUpgradeRamStickPickup } from "./ramPickup.js";

function isHumanWorldPaused() {
    return isGameplayPaused();
}

/**
 * Human Mechanic AI — KAPLAY sentry for vision, custom patrol cycle for wait/face/rotate.
 *
 * Patrol cycle example:
 * {
 *   loop: true,
 *   speed: 40,
 *   turnSpeed: 220,
 *   rotate: "smooth", // smooth | instant | cw | ccw
 *   steps: [
 *     { x, y, face, wait, rotate, turnSpeed },
 *     { face, wait, rotate: "cw" },
 *     { wait },
 *   ],
 * }
 */

const MECHANIC_FRAME_HALF_HEIGHT = 64;
const MECHANIC_VISUAL_SCALE = 1.14;
const MECHANIC_VISUAL_Y_OFFSET = -MECHANIC_FRAME_HALF_HEIGHT * MECHANIC_VISUAL_SCALE;
const HUMAN_FOOT_HITBOX_WIDTH = 12;
const HUMAN_FOOT_HITBOX_HEIGHT = 8;
const HUMAN_FOOT_HITBOX_Y = -HUMAN_FOOT_HITBOX_HEIGHT / 2;
const HUMAN_ALERT_ICON_Y = -132;
const HUMAN_ALERT_ICON_SIZE = 32;
const HUMAN_Z = 9712;
const HUMAN_SHADOW_Z = 9709;
const HUMAN_ALERT_Z = 9730;
const HUMAN_DUST_INTERVAL = 0.08;
const HUMAN_ALERT_RUMBLE = 1.8;
const HUMAN_FLEE_OFFSCREEN_MARGIN = 120;
const HUMAN_FLEE_FAILSAFE_SECONDS = 5.5;
const HUMAN_INVESTIGATE_RADIUS = 42;
const HUMAN_INVESTIGATE_WAIT = 0.7;
const HUMAN_PATH_NODE_RADIUS = 7;
const HUMAN_DIALOGUE_PAUSE_SECONDS = 0.45;
const VISION_RAY_COUNT = 15;
const VISION_RAY_Z = 9704;
const VISION_TORCH_MIN_WIDTH = 3;
const VISION_TORCH_MAX_WIDTH = 12;
const VISION_RAY_UPDATE_INTERVAL = 0.08;
const HUMAN_DETECTION_UPDATE_INTERVAL = 0.08;

let humanDialoguePauseId = 0;

const MECHANIC_ANIMS = {
    idle: { sprite: "mechanic_idle", anim: "idle" },
    walk: { sprite: "mechanic_walk", anim: "walk" },
    hurt: { sprite: "mechanic_hurt", anim: "hurt" },
    run: { sprite: "mechanic_run", anim: "run" },
};

const HUMAN_SPRITE_SETS = {
    mechanic: MECHANIC_ANIMS,
    suite: {
        idle: { sprite: "suite_idle", anim: "idle" },
        walk: { sprite: "suite_walk", anim: "walk" },
        hurt: { sprite: "suite_hurt", anim: "hurt" },
        run: { sprite: "suite_run", anim: "run" },
    },
};

function normalizeAngle(deg) {
    return ((deg % 360) + 360) % 360;
}

function shortestDiff(from, to) {
    return ((to - from + 540) % 360) - 180;
}

function cwRemaining(from, to) {
    return ((to - from) % 360 + 360) % 360;
}

function ccwRemaining(from, to) {
    return ((from - to) % 360 + 360) % 360;
}

function rotateToward(from, to, maxDelta, type) {
    const rotate = type || "smooth";

    if (rotate === "instant" || rotate === "snap") {
        return normalizeAngle(to);
    }

    if (rotate === "cw") {
        const rem = cwRemaining(from, to);
        if (rem <= maxDelta || rem < 0.5) return normalizeAngle(to);
        return normalizeAngle(from + maxDelta);
    }

    if (rotate === "ccw") {
        const rem = ccwRemaining(from, to);
        if (rem <= maxDelta || rem < 0.5) return normalizeAngle(to);
        return normalizeAngle(from - maxDelta);
    }

    // KAPLAY lerpAngle uses t in [0,1]
    const diff = Math.abs(shortestDiff(from, to));
    if (diff < 0.5) return normalizeAngle(to);
    const t = Math.min(1, maxDelta / diff);
    return normalizeAngle(lerpAngle(from, to, t));
}

function isFacing(from, to, type) {
    const rotate = type || "smooth";
    if (rotate === "instant" || rotate === "snap") return true;
    if (rotate === "cw") return cwRemaining(from, to) < 0.5;
    if (rotate === "ccw") return ccwRemaining(from, to) < 0.5;
    return Math.abs(shortestDiff(from, to)) < 0.5;
}

function normalizePatrol(patrol, defaults) {
    if (!patrol || !Array.isArray(patrol.steps) || patrol.steps.length === 0) {
        return null;
    }

    const defaultRotate = patrol.rotate || "smooth";

    return {
        loop: patrol.loop !== false,
        speed: patrol.speed ?? defaults.patrolSpeed,
        turnSpeed: patrol.turnSpeed ?? defaults.turnSpeed,
        rotate: defaultRotate,
        steps: patrol.steps.map((step) => ({
            x: step.x,
            y: step.y,
            face: step.face,
            wait: step.wait ?? 0,
            rotate: step.rotate || defaultRotate,
            turnSpeed: step.turnSpeed,
        })),
    };
}

function setHumanAnimation(human, state) {
    if (!human.visual?.exists?.()) return;

    const anims = human.anims || MECHANIC_ANIMS;
    const next = anims[state] || anims.idle || MECHANIC_ANIMS.idle;
    const facingDir = Vec2.fromAngle(human.facingAngle);
    const facingLeft = facingDir.x < -0.05;

    if (human.visualState !== state || human.visual.sprite !== next.sprite) {
        human.visual.use(sprite(next.sprite, {
            anim: next.anim,
            flipX: facingLeft,
        }));
        if (human.shadow?.exists?.()) {
            human.shadow.use(sprite(next.sprite, {
                anim: next.anim,
                flipX: facingLeft,
                flipY: true,
            }));
        }
        human.visualState = state;
    } else if (human.visual.getCurAnim()?.name !== next.anim) {
        human.visual.play(next.anim);
        if (human.shadow?.exists?.() && human.shadow.getCurAnim()?.name !== next.anim) {
            human.shadow.play(next.anim);
        }
    }

    human.visual.flipX = facingLeft;
    if (human.shadow?.exists?.()) {
        human.shadow.flipX = facingLeft;
        human.shadow.flipY = true;
    }

    // Re-apply pause after sprite swaps (new sprite comps reset animSpeed).
    if (human.animsFrozen) {
        freezeHumanAnims(human, true);
    }
}

function freezeHumanAnims(human, frozen) {
    human.animsFrozen = !!frozen;
    const speed = frozen ? 0 : 1;
    if (human.visual?.exists?.()) human.visual.animSpeed = speed;
    if (human.shadow?.exists?.()) human.shadow.animSpeed = speed;
}

function isOutsideCameraView(worldPos, margin = 0) {
    const cam = getCamPos();
    const left = cam.x - width() / 2 - margin;
    const right = cam.x + width() / 2 + margin;
    const top = cam.y - height() / 2 - margin;
    const bottom = cam.y + height() / 2 + margin;
    return worldPos.x < left || worldPos.x > right || worldPos.y < top || worldPos.y > bottom;
}

function asVec(p) {
    if (!p) return null;
    if (typeof p.x === "number" && typeof p.y === "number") {
        return vec2(p.x, p.y);
    }
    return null;
}

function getPathTo(fromPos, toPos) {
    const target = resolveWalkablePos(toPos);
    const level = getLevel();
    if (level && typeof level.getPath === "function") {
        try {
            const path = level.getPath(fromPos, target, { allowDiagonals: true })
                .map((p) => asVec(p))
                .filter(Boolean);
            if (path.length > 0) return path;
        } catch {
            // Fall through to direct target below.
        }
    }
    return [target];
}

function moveHumanOnWalkable(human, delta) {
    const nextPos = human.pos.add(delta);
    if (isHumanFootBoxWalkable(nextPos)) {
        human.pos = nextPos;
        return true;
    }

    // Axis slides so knockback stops at walls instead of ignoring the shove entirely.
    if (delta.x !== 0) {
        const onlyX = vec2(human.pos.x + delta.x, human.pos.y);
        if (isHumanFootBoxWalkable(onlyX)) {
            human.pos = onlyX;
            return true;
        }
    }
    if (delta.y !== 0) {
        const onlyY = vec2(human.pos.x, human.pos.y + delta.y);
        if (isHumanFootBoxWalkable(onlyY)) {
            human.pos = onlyY;
            return true;
        }
    }
    return false;
}

function isHumanFootBoxWalkable(originPos) {
    const left = -HUMAN_FOOT_HITBOX_WIDTH / 2 + 1;
    const right = HUMAN_FOOT_HITBOX_WIDTH / 2 - 1;
    const top = HUMAN_FOOT_HITBOX_Y + 1;
    const bottom = HUMAN_FOOT_HITBOX_Y + HUMAN_FOOT_HITBOX_HEIGHT - 1;
    const samples = [
        vec2(0, top),
        vec2(left, bottom),
        vec2(0, bottom),
        vec2(right, bottom),
    ];

    return samples.every((offset) => isWalkableWorldPos(originPos.add(offset)));
}

function followPath(human, path, speed, dtVal, radius = HUMAN_PATH_NODE_RADIUS) {
    if (!Array.isArray(path) || path.length === 0) return false;

    while (path.length > 0 && human.pos.dist(path[0]) < radius) {
        path.shift();
    }

    const nextTarget = path[0];
    if (!nextTarget) return false;

    const dir = nextTarget.sub(human.pos);
    if (dir.len() <= 0.1) return false;

    const step = dir.unit().scale(Math.min(speed * dtVal, dir.len()));
    return moveHumanOnWalkable(human, step);
}

function buildInvestigationPatrol(targetPos, cfg) {
    const center = resolveWalkablePos(targetPos);
    const offsets = [
        vec2(0, 0),
        vec2(HUMAN_INVESTIGATE_RADIUS, 0),
        vec2(0, HUMAN_INVESTIGATE_RADIUS),
        vec2(-HUMAN_INVESTIGATE_RADIUS, 0),
        vec2(0, -HUMAN_INVESTIGATE_RADIUS),
    ];
    const points = offsets
        .map((offset) => center.add(offset))
        .filter((p) => isWalkableWorldPos(p));

    const patrolPoints = points.length >= 2 ? points : [center];
    return normalizePatrol({
        loop: true,
        speed: cfg.patrolSpeed,
        turnSpeed: cfg.turnSpeed,
        steps: patrolPoints.map((p, idx) => ({
            x: p.x,
            y: p.y,
            face: idx === 0 ? 0 : undefined,
            wait: HUMAN_INVESTIGATE_WAIT,
        })),
    }, cfg);
}

export function spawnHuman(options = {}) {
    if (options && typeof options.x === "number" && typeof options.y === "number") {
        const posVec = options;
        const waypoints = arguments[1] || [];
        const facingAngle = arguments[2] ?? 90;
        options = {
            pos: posVec,
            facingAngle,
            patrol: waypoints.length > 0
                ? { steps: waypoints.map((wp) => ({ x: wp.x, y: wp.y })) }
                : null,
        };
    }

    const cfg = GAME_CONFIG.humanAI;
    const posVec = resolveWalkablePos(options.pos || vec2(0, 0));
    const facingAngle = options.facingAngle ?? 0;
    const patrol = normalizePatrol(options.patrol, cfg);
    const anims = HUMAN_SPRITE_SETS[options.spriteSet] || HUMAN_SPRITE_SETS.mechanic;

    const human = add([
        rect(16, 16),
        color(220, 160, 100),
        opacity(0),
        pos(posVec),
        anchor("center"),
        // Foot-only collision keeps the tall mechanic sprite from snagging walls.
        area({
            shape: new Rect(
                vec2(-HUMAN_FOOT_HITBOX_WIDTH / 2, HUMAN_FOOT_HITBOX_Y),
                HUMAN_FOOT_HITBOX_WIDTH,
                HUMAN_FOOT_HITBOX_HEIGHT,
            ),
        }),
        body({
            isStatic: true,
            collisionIgnore: ["human", "guard_robot"],
        }),
        z(HUMAN_Z),
        "human",
        {
            facingAngle,
            visionRadius: options.visionRadius ?? cfg.visionRadius,
            visionConeAngle: options.visionConeAngle ?? cfg.visionConeAngle,
            isAlerted: false,
            undetectedTimer: 0,
            knockbackVelocity: vec2(0, 0),
            dustTimer: 0,
            isAlarmPaused: false,
            alarmPauseTimer: 0,
            isFleeing: false,
            fleeDir: Vec2.fromAngle(facingAngle),
            fleeTarget: null,
            fleePath: [],
            fleeTimer: HUMAN_FLEE_FAILSAFE_SECONDS,
            investigatePath: [],
            investigateTarget: null,
            investigatePauseTimer: 0,
            patrol,
            patrolPath: [],
            patrolStepIdx: 0,
            patrolPhase: "move",
            patrolWaitTimer: 0,
            patrolStuckTimer: 0,
            patrolLastPos: vec2(posVec.x, posVec.y),
            alarmLockRoomId: null,
            visualState: "idle",
            anims,
            ramDropType: options.ramDropType || "first",
            ramUnlockModuleIds: options.ramUnlockModuleIds || [],
            proximityAlarm: options.proximityAlarm || null,
            visionRayTimer: Math.random() * VISION_RAY_UPDATE_INTERVAL,
            detectionTimer: Math.random() * HUMAN_DETECTION_UPDATE_INTERVAL,
            playerDetected: false,
        },
    ]);

    human.shadow = human.add([
        sprite(anims.idle.sprite, {
            anim: anims.idle.anim,
            flipY: true,
        }),
        pos(0, 4),
        anchor("center"),
        scale(MECHANIC_VISUAL_SCALE * 0.95, MECHANIC_VISUAL_SCALE * 0.24),
        color(0, 0, 0),
        opacity(0.24),
        z(HUMAN_SHADOW_Z),
    ]);

    human.visual = human.add([
        sprite(anims.idle.sprite, { anim: anims.idle.anim }),
        pos(0, MECHANIC_VISUAL_Y_OFFSET),
        anchor("center"),
        scale(MECHANIC_VISUAL_SCALE),
        color(255, 255, 255),
        z(HUMAN_Z),
    ]);

    human.alertIcon = human.add([
        text("!", { font: "ShareTechMono", size: HUMAN_ALERT_ICON_SIZE }),
        pos(0, HUMAN_ALERT_ICON_Y),
        anchor("center"),
        color(255, 235, 60),
        opacity(0),
        z(HUMAN_ALERT_Z),
    ]);

    human.onCollideUpdate("player", (p) => {
        if (isHumanWorldPaused() || isHumanDetectionGraceActive()) return;
        if (p.isDigging) return;
        triggerHumanAlarm(human, "Contact!");
    });

    const coneLineColor = rgb(180, 190, 205);
    const coneAlertColor = rgb(255, 70, 70);

    const visionRays = Array.from({ length: VISION_RAY_COUNT }, (_, i) => {
        const edgeRay = i === 0 || i === VISION_RAY_COUNT - 1;
        return human.add([
            rect(human.visionRadius, 1),
            pos(0, 0),
            anchor("left"),
            rotate(0),
            scale(1, 1),
            color(coneLineColor),
            opacity(0),
            z(VISION_RAY_Z),
        ]);
    });

    function setVisionRays(facing, halfAngle, baseOpacity, lineColor) {
        if (baseOpacity <= 0) {
            for (const ray of visionRays) {
                ray.opacity = 0;
            }
            return;
        }

        for (let i = 0; i < VISION_RAY_COUNT; i++) {
            const ray = visionRays[i];
            if (!ray?.exists?.()) continue;

            const t = VISION_RAY_COUNT === 1 ? 0.5 : i / (VISION_RAY_COUNT - 1);
            const rayAngle = facing - halfAngle + t * halfAngle * 2;
            const end = getSightEndPoint(human.pos, rayAngle, human.visionRadius);
            const rayLength = end ? human.pos.dist(end) : 0;
            if (rayLength < 2) {
                ray.opacity = 0;
                continue;
            }

            const edgeRay = i === 0 || i === VISION_RAY_COUNT - 1;
            const centerWeight = Math.max(0, 1 - Math.abs(t - 0.5) * 2);
            const rayWidth = edgeRay
                ? VISION_TORCH_MIN_WIDTH
                : VISION_TORCH_MIN_WIDTH + centerWeight * (VISION_TORCH_MAX_WIDTH - VISION_TORCH_MIN_WIDTH);
            ray.angle = rayAngle;
            ray.scale = vec2(rayLength / human.visionRadius, rayWidth);
            ray.color = lineColor;
            ray.opacity = baseOpacity * (edgeRay ? 0.55 : 0.18 + centerWeight * 0.58);
        }
    }

    function setAlertIcon(label, opacityValue) {
        if (!human.alertIcon?.exists?.()) return;
        human.alertIcon.text = label;
        human.alertIcon.opacity = opacityValue;
    }

    function emitHumanDust(moveDir) {
        if (!moveDir || moveDir.len() <= 0) return;
        human.dustTimer -= dt();
        if (human.dustTimer > 0) return;
        human.dustTimer = HUMAN_DUST_INTERVAL;
        spawnMovementDust(human.pos, moveDir, {
            z: HUMAN_Z - 1,
            size: 1.9,
            opacity: 0.32,
            color: [96, 88, 78],
        });
    }

    function advancePatrolStep() {
        const cycle = human.patrol;
        if (!cycle) return;
        human.patrolPath = [];

        const next = human.patrolStepIdx + 1;
        if (next >= cycle.steps.length) {
            human.patrolStepIdx = cycle.loop ? 0 : cycle.steps.length - 1;
            if (!cycle.loop) {
                human.patrol = null;
                return;
            }
        } else {
            human.patrolStepIdx = next;
        }

        const step = cycle.steps[human.patrolStepIdx];
        const hasPos = typeof step.x === "number" && typeof step.y === "number";
        human.patrolPhase = hasPos ? "move" : (typeof step.face === "number" ? "turn" : "wait");
        human.patrolWaitTimer = step.wait || 0;
    }

    function updatePatrol(dtVal) {
        const cycle = human.patrol;
        if (!cycle || human.isAlerted) return;

        const step = cycle.steps[human.patrolStepIdx];
        if (!step) return;

        const turnSpeed = step.turnSpeed ?? cycle.turnSpeed;
        const rotateType = step.rotate || cycle.rotate || "smooth";

        if (human.patrolPhase === "move") {
            const target = resolveWalkablePos(vec2(step.x, step.y));
            const dir = target.sub(human.pos);
            const dist = dir.len();

            if (dist < 4) {
                human.pos = target;
                human.patrolPath = [];
                human.patrolStuckTimer = 0;
                if (typeof step.face === "number") {
                    human.patrolPhase = "turn";
                } else if ((step.wait || 0) > 0) {
                    human.patrolPhase = "wait";
                    human.patrolWaitTimer = step.wait;
                } else {
                    advancePatrolStep();
                }
            } else {
                if (!human.patrolPath || human.patrolPath.length === 0) {
                    human.patrolPath = getPathTo(human.pos, target);
                }

                while (human.patrolPath.length > 0 && human.pos.dist(human.patrolPath[0]) < HUMAN_PATH_NODE_RADIUS) {
                    human.patrolPath.shift();
                }

                const nextTarget = human.patrolPath[0] || target;
                const moveDir = nextTarget.sub(human.pos).unit();
                human.facingAngle = rotateToward(
                    human.facingAngle,
                    moveDir.angle(),
                    turnSpeed * dtVal,
                    rotateType,
                );
                const moved = followPath(human, human.patrolPath, cycle.speed, dtVal);

                if (!moved || human.pos.dist(human.patrolLastPos) < 0.35) {
                    human.patrolStuckTimer += dtVal;
                    if (human.patrolStuckTimer > 0.7) {
                        human.patrolStuckTimer = 0;
                        human.patrolPath = [];
                        advancePatrolStep();
                    }
                } else {
                    human.patrolStuckTimer = 0;
                }
                human.patrolLastPos = vec2(human.pos.x, human.pos.y);
            }
            return;
        }

        if (human.patrolPhase === "turn") {
            const targetFace = typeof step.face === "number" ? step.face : human.facingAngle;
            human.facingAngle = rotateToward(
                human.facingAngle,
                targetFace,
                turnSpeed * dtVal,
                rotateType,
            );

            if (isFacing(human.facingAngle, targetFace, rotateType)) {
                human.facingAngle = normalizeAngle(targetFace);
                if ((step.wait || 0) > 0) {
                    human.patrolPhase = "wait";
                    human.patrolWaitTimer = step.wait;
                } else {
                    advancePatrolStep();
                }
            }
            return;
        }

        if (human.patrolPhase === "wait") {
            human.patrolWaitTimer -= dtVal;
            if (human.patrolWaitTimer <= 0) {
                advancePatrolStep();
            }
        }
    }

    function isPlayerInVisionCone(player) {
        if (!player || !player.pos) return false;

        const humanRoom = getRoomAt(human.pos);
        const playerRoom = getRoomAt(player.pos);
        if (!humanRoom || !playerRoom || humanRoom.id !== playerRoom.id) return false;

        const toPlayer = player.pos.sub(human.pos);
        const dist = toPlayer.len();
        if (dist <= 0 || dist > human.visionRadius) return false;

        // Signed angle between facing and player offset (degrees)
        const facing = Vec2.fromAngle(human.facingAngle);
        const angleDiff = Math.abs(facing.angleBetween(toPlayer));
        if (angleDiff > human.visionConeAngle / 2) return false;

        return hasLineOfSightWorld(human.pos, player.pos);
    }

    function isPlayerInProximityAlarm(player) {
        if (!player || !player.pos || !human.proximityAlarm) return false;

        const playerRoom = getRoomAt(player.pos);
        if (!playerRoom || playerRoom.id !== human.proximityAlarm.roomId) return false;
        if (isHumanDetectionGraceActive()) return false;

        const origin = human.proximityAlarm.origin;
        const triggerDistance = human.proximityAlarm.distance;
        return origin && triggerDistance > 0 && player.pos.dist(origin) >= triggerDistance;
    }

    human.applyKnockback = (dirVec, force) => {
        human.knockbackVelocity = dirVec.scale(force);
    };

    human.startAlarmPause = () => {
        if (human.isAlarmPaused || human.isFleeing) return;

        human.isAlarmPaused = true;
        human.alarmPauseTimer = cfg.alarmPauseTime ?? 0.45;
        human.knockbackVelocity = vec2(0, 0);
        human.investigatePauseTimer = 0;
        human.investigatePath = [];
        setAlertIcon("!", 1);
        shake(HUMAN_ALERT_RUMBLE);
        setHumanAnimation(human, "hurt");
    };

    human.startFlee = () => {
        if (human.isFleeing) return;

        human.isAlarmPaused = false;
        setAlertIcon("!", 0);
        if (human.ramDropType === "upgrade") {
            spawnUpgradeRamStickPickup(vec2(human.pos.x, human.pos.y), {
                pickupKey: `upgrade_ram_${Math.round(human.pos.x)}_${Math.round(human.pos.y)}`,
                unlockModuleIds: human.ramUnlockModuleIds,
            });
        } else {
            spawnFirstRamStickPickup(vec2(human.pos.x, human.pos.y));
        }

        const player = get("player")[0];
        let fleeDir = player?.pos ? human.pos.sub(player.pos) : Vec2.fromAngle(human.facingAngle);
        if (fleeDir.len() <= 0.1) {
            fleeDir = Vec2.fromAngle(human.facingAngle);
        }

        const fleeTarget = getFleeTargetAwayFrom(human.pos, player?.pos);
        const level = getLevel();
        let fleePath = [];
        if (level && typeof level.getPath === "function") {
            try {
                fleePath = level.getPath(human.pos, fleeTarget, { allowDiagonals: true })
                    .map((p) => asVec(p))
                    .filter(Boolean);
            } catch {
                fleePath = [];
            }
        }

        human.isFleeing = true;
        human.fleeDir = fleeDir.unit();
        human.fleeTarget = fleeTarget;
        human.fleePath = fleePath.length > 0 ? fleePath : [fleeTarget];
        human.fleeTimer = HUMAN_FLEE_FAILSAFE_SECONDS;
        human.patrol = null;
        human.knockbackVelocity = vec2(0, 0);
        human.facingAngle = human.fleeDir.angle();
        setHumanAnimation(human, "run");
    };

    human.startInvestigation = (targetPos) => {
        if (human.isFleeing || human.isAlarmPaused || !targetPos) return;

        const target = resolveWalkablePos(targetPos);
        human.isAlerted = false;
        human.undetectedTimer = 0;
        human.knockbackVelocity = vec2(0, 0);
        human.investigateTarget = target;
        human.investigatePauseTimer = cfg.investigatePauseTime ?? 0.45;
        human.investigatePath = [];
        human.patrol = null;
        human.patrolPath = [];
        human.patrolStepIdx = 0;
        human.patrolPhase = "move";
        human.patrolWaitTimer = 0;
        human.patrolLastPos = vec2(human.pos.x, human.pos.y);
        setAlertIcon("?", 1);
    };

    function updateInvestigation(dtVal) {
        if (!human.investigatePath || human.investigatePath.length === 0) return false;

        while (human.investigatePath.length > 0 && human.pos.dist(human.investigatePath[0]) < 7) {
            human.investigatePath.shift();
        }

        const nextTarget = human.investigatePath[0];
        if (!nextTarget) {
            human.patrol = buildInvestigationPatrol(human.investigateTarget || human.pos, cfg);
            human.patrolStepIdx = 0;
            human.patrolPhase = "move";
            human.investigateTarget = null;
            return false;
        }

        const dir = nextTarget.sub(human.pos);
        if (dir.len() <= 0.1) return false;

        const moveDir = dir.unit();
        human.facingAngle = rotateToward(
            human.facingAngle,
            moveDir.angle(),
            cfg.turnSpeed * dtVal,
            "smooth",
        );
        return followPath(human, human.investigatePath, cfg.patrolSpeed, dtVal);
    }

    // Single detection path for seeing or contacting the dog.
    onUpdate(() => {
        if (!human.exists()) return;
        if (isHumanWorldPaused()) {
            freezeHumanAnims(human, true);
            return;
        }
        freezeHumanAnims(human, false);

        if (human.isAlarmPaused) {
            human.alarmPauseTimer -= dt();
            setHumanAnimation(human, "hurt");
            setAlertIcon("!", 1);
            setVisionRays(human.facingAngle, human.visionConeAngle / 2, 0, coneLineColor);

            if (human.alarmPauseTimer <= 0) {
                human.startFlee();
            }
            return;
        }

        if (human.investigatePauseTimer > 0) {
            human.investigatePauseTimer -= dt();
            human.knockbackVelocity = vec2(0, 0);
            setAlertIcon("?", 1);
            setHumanAnimation(human, "idle");
            setVisionRays(human.facingAngle, human.visionConeAngle / 2, 0, coneLineColor);

            if (human.investigatePauseTimer <= 0) {
                setAlertIcon("?", 0);
                if (human.investigateTarget) {
                    human.investigatePath = getPathTo(human.pos, human.investigateTarget);
                }
            }
            return;
        }

        if (human.isFleeing) {
            human.fleeTimer -= dt();
            while (human.fleePath.length > 0 && human.pos.dist(human.fleePath[0]) < 8) {
                human.fleePath.shift();
            }

            const nextTarget = human.fleePath[0] || human.fleeTarget;
            const fleeStep = nextTarget ? nextTarget.sub(human.pos) : human.fleeDir;
            if (fleeStep.len() > 0.1) {
                human.fleeDir = fleeStep.unit();
                const beforePos = vec2(human.pos.x, human.pos.y);
                const moved = followPath(human, human.fleePath, cfg.fleeSpeed ?? 180, dt(), 8);
                if (!moved) {
                    human.fleePath = getPathTo(human.pos, human.fleeTarget || human.pos);
                } else {
                    emitHumanDust(human.pos.sub(beforePos));
                }
                human.facingAngle = human.fleeDir.angle();
            }
            setHumanAnimation(human, "run");
            setVisionRays(human.facingAngle, human.visionConeAngle / 2, 0, coneLineColor);

            const reachedExit = human.fleePath.length === 0 && human.fleeTarget && human.pos.dist(human.fleeTarget) < 12;
            if (reachedExit || human.fleeTimer <= 0 || isOutsideCameraView(human.pos, HUMAN_FLEE_OFFSCREEN_MARGIN)) {
                releaseHumanAlarmLock(human);
                destroy(human);
            }
            return;
        }

        let isMoving = false;
        if (human.knockbackVelocity.len() > 5) {
            const beforePos = vec2(human.pos.x, human.pos.y);
            const moved = moveHumanOnWalkable(human, human.knockbackVelocity.scale(dt()));
            if (!moved) {
                human.knockbackVelocity = vec2(0, 0);
            } else {
                emitHumanDust(human.pos.sub(beforePos));
            }
            human.knockbackVelocity = human.knockbackVelocity.scale(0.85);
            isMoving = moved;
        } else if (human.investigatePath?.length > 0) {
            const beforePos = vec2(human.pos.x, human.pos.y);
            isMoving = updateInvestigation(dt());
            if (isMoving) emitHumanDust(human.pos.sub(beforePos));
        } else if (human.patrol) {
            const beforePos = vec2(human.pos.x, human.pos.y);
            updatePatrol(dt());
            isMoving = human.pos.dist(beforePos) > 0.1;
            if (isMoving) emitHumanDust(human.pos.sub(beforePos));
        }

        setHumanAnimation(human, isMoving ? "walk" : "idle");

        const opticsLoaded = isLoaded("sensor_optics");
        const baseOpacity = human.isAlerted
            ? (opticsLoaded ? 0.72 : 0.34)
            : (opticsLoaded ? 0.5 : 0.22);
        const lineColor = human.isAlerted ? coneAlertColor : coneLineColor;
        human.visionRayTimer -= dt();
        if (human.visionRayTimer <= 0) {
            human.visionRayTimer = VISION_RAY_UPDATE_INTERVAL;
            setVisionRays(human.facingAngle, human.visionConeAngle / 2, baseOpacity, lineColor);
        }

        const player = get("player")[0];
        human.detectionTimer -= dt();
        if (human.detectionTimer <= 0) {
            human.detectionTimer = HUMAN_DETECTION_UPDATE_INTERVAL;
            human.playerDetected = (!isHumanDetectionGraceActive() && isPlayerInVisionCone(player))
                || isPlayerInProximityAlarm(player);
        }
        const detecting = human.playerDetected;

        if (detecting) {
            triggerHumanAlarm(human, "Intruder spotted!");
        }

        if (human.isAlerted) {
            if (detecting) {
                human.undetectedTimer = 0;
            } else {
                human.undetectedTimer += dt();
                if (human.undetectedTimer >= (cfg.alertCalmTime ?? 5)) {
                    human.isAlerted = false;
                    human.undetectedTimer = 0;
                    releaseHumanAlarmLock(human);
                }
            }
        }
    });

    return human;
}

export function triggerHumanAlarm(human, reasonText) {
    if (!human) return;

    const wasAlerted = human.isAlerted;
    human.isAlerted = true;
    human.undetectedTimer = 0;

    const humanRoom = getRoomAt(human.pos);
    if (humanRoom && human.alarmLockRoomId == null) {
        human.alarmLockRoomId = humanRoom.id;
        lockAlarmRoom(human.alarmLockRoomId);
    }

    getRobotsLinkedToHuman(human).forEach((robot) => {
        if (robot.activate) robot.activate();
    });

    if (!wasAlerted) {
        playAt(AUDIO_SOUNDS.humanAlert, human.pos);
        playAlarmFromLights(human.alarmLockRoomId);
    }

    if (!wasAlerted && human.startAlarmPause) {
        const pauseId = `human_dialogue_pause_${humanDialoguePauseId++}`;
        lockCutscene(pauseId);
        wait(HUMAN_DIALOGUE_PAUSE_SECONDS, () => {
            unlockCutscene(pauseId);
        });
        showHumanDialogue(human);
        human.startAlarmPause(reasonText);
    }
}

function releaseHumanAlarmLock(human) {
    if (human?.alarmLockRoomId == null) return;
    human.alarmLockRoomId = null;
}

export function getRobotsLinkedToHuman(human) {
    if (!human?.exists?.()) return [];

    const humanRoom = getRoomAt(human.pos);
    if (!humanRoom) return [];

    return get("guard_robot", { recursive: true }).filter((robot) => {
        if (!robot.exists?.()) return false;
        const robotRoom = getRoomAt(robot.pos);
        return robotRoom?.id === humanRoom.id;
    });
}

export function attractClosestHumanTo(worldPos) {
    const target = asVec(worldPos);
    if (!target) return null;
    const targetRoom = getRoomAt(target);
    if (!targetRoom) return null;

    let closest = null;
    let closestDist = Infinity;

    get("human", { recursive: true }).forEach((human) => {
        if (!human.exists() || human.isFleeing) return;
        const humanRoom = getRoomAt(human.pos);
        if (humanRoom?.id !== targetRoom.id) return;
        const dist = human.pos.dist(target);
        if (dist < closestDist) {
            closest = human;
            closestDist = dist;
        }
    });

    if (closest?.startInvestigation) {
        closest.startInvestigation(target);
    }

    return closest;
}
